import React, { forwardRef, useEffect, useRef } from 'react';
import { observer } from 'mobx-react';
import { Box } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import {
  SnackbarApiErrorMessage,
  useDataProvider,
  useSnackbar,
} from '@ui-framework/index';
import ListBoxSelectorStatic from '../ListBoxSelectorStatic';

const IndustrySelector = forwardRef((props, ref) => {
  const leftNameKey = 'industryName';
  const leftValueKey = 'id';

  const limit = 10;

  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { get, getApiUrl } = useDataProvider(provider);
  const snackbar = useSnackbar();

  const { control, getValues } = useFormContext();
  const initialValue = getValues(props.name) || [];

  const rightItemsRef = useRef(initialValue || []);

  const getLeftItems = async page => {
    const response = await get(
      `${getApiUrl()}/sxcl/v1/industries?page=${page}&limit=${limit}`,
    );
    if (response.ok) {
      const { data = [], ...other } = response.data;

      const resultsData =
        data?.map(item => ({
          label: item[leftNameKey],
          value: item[leftValueKey],
        })) || [];
      return { data: resultsData, ...other };
    }
    snackbar.showMessage({
      type: 'error',
      data: <SnackbarApiErrorMessage errors={response?.errors} />,
    });
    return [];
  };

  const getRightItems = pageNumber => {
    const limitPerPage = 10;
    const totalPages = Math.ceil(rightItemsRef.current.length / limitPerPage);
    const response = {
      data: rightItemsRef.current.slice(
        (pageNumber - 1) * limitPerPage,
        pageNumber * limitPerPage,
      ),
      total: rightItemsRef.current.length,
      page: pageNumber,
      limit: limitPerPage,
      totalPages,
    };
    return response;
  };

  const onItemsAdded = async items => {
    if (Array.isArray(items)) {
      rightItemsRef.current = [...rightItemsRef.current, ...items];
    }
    return rightItemsRef.current;
  };

  const onItemsRemoved = async items => {
    if (items === null || !Array.isArray(items)) {
      rightItemsRef.current = [];
    } else {
      const foundItems = rightItemsRef.current.filter(
        item => !items.some(removed => removed.value === item.value),
      );
      rightItemsRef.current = foundItems?.length > 0 ? foundItems : [];
    }
    return rightItemsRef.current;
  };

  useEffect(() => {
    if (rightItemsRef?.current) {
      rightItemsRef.current = initialValue;
    }
  }, [initialValue]);

  return (
    <Box ref={ref}>
      <Controller
        control={control}
        name={props?.name}
        render={({ field: controllerField }) => {
          return (
            <ListBoxSelectorStatic
              rightInitial={initialValue}
              hasSelectAll={false}
              height="390px"
              getLeftItems={getLeftItems}
              getRightItems={getRightItems}
              parentId={null}
              onItemsAdded={async data => {
                const newItems = await onItemsAdded(data);
                const e = {
                  target: {
                    name: controllerField?.name,
                    value: newItems,
                  },
                };
                controllerField.onChange(e);
              }}
              onItemsRemoved={async data => {
                const newItems = await onItemsRemoved(data);
                const e = {
                  target: {
                    name: controllerField?.name,
                    value: newItems,
                  },
                };
                controllerField.onChange(e);
              }}
            />
          );
        }}
      />
    </Box>
  );
});

export default observer(IndustrySelector);
